/**
 * Identity-preserving merge helpers for reducers fed by server pushes.
 *
 * PERF - every WS game push used to rebuild `state.game`, `state.players` and
 * the derived arrays wholesale, so EVERY selected value changed identity on
 * EVERY message and every subscribed component re-rendered whether or not the
 * data it displays changed. On a live table that render fan-out measured
 * 50-68ms of blocked main thread per message batch (3-4 dropped frames at
 * 60fps, the dominant remaining hitch after the context memoization pass).
 * See docs/investigations/20260819-game-client-overheating.md (section 8f).
 *
 * These helpers only WRITE a draft property when the incoming value is not
 * deeply equal to what is already there. Immer treats an untouched property
 * as unchanged and preserves its identity, so react-redux subscribers whose
 * selected slice did not actually change never re-render. Deep comparison is
 * cheap here because game payloads are small (a few KB of mostly scalars).
 */

import { isDraft, original } from 'immer';

/**
 * Read a draft's CURRENT-value view for comparison without touching the
 * draft. Reading nested objects through an Immer draft proxy materializes
 * child drafts (real allocation + proxy overhead on every read - measured as
 * ~4ms and ~3MB of heap churn per WS message when comparing through the
 * proxies directly). `original()` returns the plain pre-dispatch object, so
 * comparisons are cheap plain-object reads. Callers must therefore compare a
 * key BEFORE writing it in the same dispatch (each helper writes a key at
 * most once, and derived keys are excluded from the payload merge).
 */
const compareView = (draft: any): any => (isDraft(draft) ? original(draft) ?? draft : draft);

/** Deep value equality sized for server payloads (plain objects, arrays, scalars). */
export function payloadEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!payloadEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (a && b && typeof a === 'object') {
    if (Array.isArray(b)) return false;
    const ka = Object.keys(a as object);
    const kb = Object.keys(b as object);
    if (ka.length !== kb.length) return false;
    for (const k of ka) {
      if (!payloadEqual((a as any)[k], (b as any)[k])) return false;
    }
    return true;
  }
  return false;
}

/**
 * Spread-semantics merge (`{ ...draft, ...src }`) that skips writes whose
 * value is already equal, so unchanged properties keep their identity.
 */
export function mergeIfChanged(draft: any, src: any): void {
  if (!src || typeof src !== 'object') return;
  const base = compareView(draft);
  for (const k of Object.keys(src)) {
    if (!payloadEqual(base[k], src[k])) draft[k] = src[k];
  }
}

/** Assign draft[key] = next only when the value actually changed. */
export function assignIfChanged(draft: any, key: string, next: any): void {
  if (!payloadEqual(compareView(draft)[key], next)) draft[key] = next;
}

/**
 * Replace an array element-by-element so UNCHANGED elements keep their
 * identity even when a sibling changed. One player acting then re-renders
 * only that player's seat, not all nine.
 */
export function mergeArrayItems(draft: any, key: string, next: unknown): void {
  const cur = compareView(draft)[key];
  // A payload that omits the key entirely is a PARTIAL update, not an
  // instruction to empty the collection. Writing the missing value straight
  // through used to set `state.players = undefined`, and
  // components/Table/Seats.tsx maps `players.slice(1)` unguarded — so one such
  // payload took the whole felt down to the global error boundary. Keeping the
  // last known-good array degrades to a stale felt instead, which the usability
  // cross-check (utils/tableUsable) can then judge on its merits.
  //
  // `next` is typed `unknown` precisely because callers can reach here with a
  // missing value: reducers/currentTable's `updateCurrentTable` destructures
  // `players` with no default.
  if (!Array.isArray(next)) return;
  if (!Array.isArray(cur)) {
    draft[key] = next;
    return;
  }
  for (let i = 0; i < next.length; i++) {
    if (!payloadEqual(cur[i], next[i])) draft[key][i] = next[i];
  }
  if (cur.length > next.length) draft[key].length = next.length;
}
